<script setup>
import { ref, computed, onMounted } from 'vue';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Integration from './Integration.vue';
import integrationAPI from 'dashboard/api/integrations';

import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const integrationLoaded = ref(false);
const storeUrl = ref('');
const consumerKey = ref('');
const consumerSecret = ref('');
const isSubmitting = ref(false);
const formError = ref('');
const integration = useFunctionGetter(
  'integrations/getIntegration',
  'woocommerce'
);
const uiFlags = useMapGetter('integrations/getUIFlags');

const integrationAction = computed(() => {
  if (integration.value.enabled) {
    return 'disconnect';
  }
  return 'connect';
});

const hideConnectModal = () => {
  storeUrl.value = '';
  consumerKey.value = '';
  consumerSecret.value = '';
  formError.value = '';
  isSubmitting.value = false;
};

const validateStoreUrl = url => {
  try {
    const normalized = url.startsWith('http') ? url : `https://${url}`;
    const parsed = new URL(normalized);
    return !!parsed.host;
  } catch {
    return false;
  }
};

const openConnectDialog = () => {
  if (dialogRef.value) {
    dialogRef.value.open();
  }
};

const handleConnectSubmit = async () => {
  try {
    formError.value = '';
    if (!validateStoreUrl(storeUrl.value)) {
      formError.value = t('INTEGRATION_SETTINGS.WOOCOMMERCE.STORE_URL.INVALID');
      return;
    }
    if (!consumerKey.value || !consumerSecret.value) {
      formError.value = t('INTEGRATION_SETTINGS.WOOCOMMERCE.CREDENTIALS.REQUIRED');
      return;
    }

    isSubmitting.value = true;
    await integrationAPI.connectWooCommerce({
      storeUrl: storeUrl.value,
      consumerKey: consumerKey.value,
      consumerSecret: consumerSecret.value,
    });

    await store.dispatch('integrations/get', 'woocommerce');
    hideConnectModal();
    if (dialogRef.value) {
      dialogRef.value.close();
    }
  } catch (error) {
    formError.value =
      error.response?.data?.error ||
      t('INTEGRATION_SETTINGS.WOOCOMMERCE.ERROR');
  } finally {
    isSubmitting.value = false;
  }
};

const initializeWooCommerceIntegration = async () => {
  await store.dispatch('integrations/get', 'woocommerce');
  integrationLoaded.value = true;
};

onMounted(() => {
  initializeWooCommerceIntegration();
});
</script>

<template>
  <SettingsLayout :is-loading="!integrationLoaded">
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.WOOCOMMERCE.HEADER')"
        description=""
        feature-name="woocommerce_integration"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <Integration
          :integration-id="integration.id"
          :integration-logo="integration.logo"
          :integration-name="integration.name"
          :integration-description="integration.description"
          :integration-enabled="integration.enabled"
          :integration-action="integrationAction"
          :delete-confirmation-text="{
            title: t('INTEGRATION_SETTINGS.WOOCOMMERCE.DELETE.TITLE'),
            message: t('INTEGRATION_SETTINGS.WOOCOMMERCE.DELETE.MESSAGE'),
          }"
        >
          <template #action>
            <Button
              teal
              :label="t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
              @click="openConnectDialog"
            />
          </template>
        </Integration>
        <Dialog
          ref="dialogRef"
          :title="t('INTEGRATION_SETTINGS.WOOCOMMERCE.CONNECT.TITLE')"
          :is-loading="isSubmitting"
          @confirm="handleConnectSubmit"
          @close="hideConnectModal"
        >
          <div class="flex flex-col gap-4">
            <Input
              v-model="storeUrl"
              :label="t('INTEGRATION_SETTINGS.WOOCOMMERCE.STORE_URL.LABEL')"
              :placeholder="
                t('INTEGRATION_SETTINGS.WOOCOMMERCE.STORE_URL.PLACEHOLDER')
              "
              :message="
                !formError
                  ? t('INTEGRATION_SETTINGS.WOOCOMMERCE.STORE_URL.HELP')
                  : formError
              "
              :message-type="formError ? 'error' : 'info'"
            />
            <Input
              v-model="consumerKey"
              :label="t('INTEGRATION_SETTINGS.WOOCOMMERCE.CREDENTIALS.KEY_LABEL')"
              :placeholder="
                t('INTEGRATION_SETTINGS.WOOCOMMERCE.CREDENTIALS.KEY_PLACEHOLDER')
              "
            />
            <Input
              v-model="consumerSecret"
              type="password"
              :label="
                t('INTEGRATION_SETTINGS.WOOCOMMERCE.CREDENTIALS.SECRET_LABEL')
              "
              :placeholder="
                t(
                  'INTEGRATION_SETTINGS.WOOCOMMERCE.CREDENTIALS.SECRET_PLACEHOLDER'
                )
              "
            />
          </div>
        </Dialog>
      </div>
    </template>
  </SettingsLayout>
</template>
